import { Link } from "react-router-dom";
import { ArrowUpRight, Cog, ShieldCheck, Layers, GitBranch } from "lucide-react";

const techHighlights = [
  "Custom AI Integration",
  "Multi-Tenant Cloud Architectures",
  "High-Velocity Frontend Frameworks",
  "API Design & Database Optimization",
];

const execHighlights = [
  "Agile Sprint Architecture",
  "Milestone Scoping & Mitigation",
  "Cross-Platform Product Mapping",
  "Flawless Stakeholder Transparency",
];

const deploymentLogs = [
  { t: "00:00:01", tag: "OK", msg: "scope.lock() → product brief verified" },
  { t: "00:00:04", tag: "OK", msg: "engineering.assign(Lao) · stack chosen" },
  { t: "00:00:06", tag: "OK", msg: "execution.assign(Sherrie) · sprint plan generated" },
  { t: "00:00:12", tag: "OK", msg: "deploy.preview → staging.traceelements.io" },
  { t: "00:00:18", tag: "OK", msg: "tests.suite(passed: 248/248)" },
  { t: "00:00:22", tag: "OK", msg: "client.review → revision queued" },
  { t: "00:00:30", tag: "DEPLOY", msg: "production.push → vSemver.lock" },
];

export default function TheEngine() {
  return (
    <div className="bg-[#0B0C10]">
      {/* Hero */}
      <section className="relative border-b border-[#1F2833] py-20 lg:py-28 overflow-hidden" data-testid="engine-hero">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">
            // THE.ENGINE — the foundation
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            ELITE SYNERGY. <br />
            <span className="text-[#FF3355]">ZERO FRICTION.</span>
          </h1>
          <p className="mt-8 text-[#C5C6C7] max-w-2xl leading-relaxed">
            Most agencies fail because the sales team promises what the engineering team can&rsquo;t
            build, and the project managers don&rsquo;t understand the code. At TraceElements, the
            co-founders <em className="text-[#F5F5F7] not-italic">are</em> the builders.
          </p>
        </div>
      </section>

      {/* Sticky split */}
      <section className="border-b border-[#1F2833] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Sticky left */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">
              // THE FOUNDATION
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
              How we eliminated the corporate telephone game.
            </h2>
            <p className="mt-6 text-[#C5C6C7] leading-relaxed">
              By coupling elite full-stack engineering directly with rigorous Agile project
              management, we deliver complex software at a velocity standard agencies cannot match.
            </p>
            <div className="mt-8 border-l-2 border-[#FF3355] bg-[#1F2833]/40 p-5 font-mono text-xs text-[#C5C6C7]">
              <div className="text-[#FF3355]">$ workflow --map</div>
              <div className="mt-2">scope → architect → sprint → deploy → iterate</div>
            </div>

            <Link
              to="/terminal"
              data-testid="engine-cta-launch"
              className="mt-10 inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors"
            >
              Initialize Project <ArrowUpRight size={14} />
            </Link>
          </aside>

          {/* Scrolling content */}
          <div className="lg:col-span-8 space-y-16">
            {/* Section: Technical */}
            <div data-testid="engine-section-technical">
              <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C]">
                <Cog size={14} /> // TECHNICAL ARCHITECTURE & STACK DEVELOPMENT
              </div>
              <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold tracking-tight">
                Managed directly by Lao Dara.
              </h3>
              <p className="mt-5 text-[#C5C6C7] leading-relaxed">
                We approach software from a systems-generalist perspective. Every app, platform, or
                custom AI solution is engineered with future-proof scalability, data optimization,
                and tight security protocols at its core.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#1F2833] border border-[#1F2833]">
                {techHighlights.map((h) => (
                  <div key={h} className="te-lime-accent bg-[#0B0C10] p-5 font-mono text-sm text-[#F5F5F7] flex items-center gap-3">
                    <ShieldCheck size={14} className="text-emerald-400" />
                    {h}
                  </div>
                ))}
              </div>
            </div>

            {/* Section: Strategic */}
            <div data-testid="engine-section-strategic">
              <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C]">
                <GitBranch size={14} /> // STRATEGIC EXECUTION & PROJECT LIFECYCLE
              </div>
              <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold tracking-tight">
                Managed directly by Sherrie Dara.
              </h3>
              <p className="mt-5 text-[#C5C6C7] leading-relaxed">
                Great code is worthless if it misses the launch window. We keep production tracks
                hyper-organized, transparent, and completely aligned with your business objectives.
                You will always know exactly what is being built, when it will deploy, and how it
                performs.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#1F2833] border border-[#1F2833]">
                {execHighlights.map((h) => (
                  <div key={h} className="te-lime-accent bg-[#0B0C10] p-5 font-mono text-sm text-[#F5F5F7] flex items-center gap-3">
                    <Layers size={14} className="text-[#FF3355]" />
                    {h}
                  </div>
                ))}
              </div>
            </div>

            {/* Deployment log */}
            <div data-testid="engine-deployment-log" className="te-terminal-window">
              <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
                  <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
                  <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
                  deployment.live · continuous-integration
                </div>
                <div className="font-mono text-[11px] text-[#FF3355]">STREAMING</div>
              </div>
              <div className="p-6 font-mono text-sm text-[#C5C6C7] space-y-2">
                {deploymentLogs.map((l) => (
                  <div key={l.t} className="grid grid-cols-[80px_60px_1fr] gap-3 items-baseline">
                    <span className="text-[#C5C6C7]/50">{l.t}</span>
                    <span className="text-[#FF3355]">[{l.tag}]</span>
                    <span>{l.msg}</span>
                  </div>
                ))}
                <div className="text-[#FF3355] pl-[150px]">&gt; awaiting next.signal<span className="te-caret">_</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
